import styled from '@emotion/styled';
import { colors } from '@innovationdepartment/proxima-ui';

export const FieldError = styled.div`
  font-size: 12px;
  color: ${colors.red60};
`;

export const NotAppliedNote = styled.div`
  font-size: 12px;
  color: ${colors.orange70};
`;

export const FieldHint = styled.div`
  font-size: 12px;
  color: ${colors.neutral50};
`;

export const Segmented = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 3px;
  border-radius: 8px;
  background: ${colors.neutral10};
  gap: 2px;
`;

export const SegmentButton = styled.button<{ selected: boolean; unavailable?: boolean }>`
  opacity: ${({ unavailable }) => (unavailable ? 0.45 : 1)};
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ selected }) => (selected ? colors.neutral100 : colors.neutral70)};
  background: ${({ selected }) => (selected ? colors.white : 'transparent')};
  box-shadow: ${({ selected }) => (selected ? '0 1px 2px rgba(0, 0, 0, 0.12)' : 'none')};
  cursor: pointer;
  transition: background 120ms ease;

  &:hover:not(:disabled) {
    color: ${colors.neutral100};
  }

  &:disabled {
    cursor: default;
  }
`;

export const RangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.neutral50};
  font-size: 13px;
`;

export const NumberBox = styled.div<{ invalid: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 36px;
  width: 132px;
  padding: 0 10px;
  border: 1px solid ${({ invalid }) => (invalid ? colors.red60 : colors.neutral30)};
  border-radius: 6px;
  background: ${colors.white};
  box-sizing: border-box;
  gap: 4px;

  &:focus-within {
    border-color: ${({ invalid }) => (invalid ? colors.red60 : colors.blue60)};
    box-shadow: 0 0 0 3px ${({ invalid }) => (invalid ? colors.red10 : colors.blue10)};
  }

  span {
    color: ${colors.neutral50};
    font-size: 13px;
  }

  input {
    border: none;
    outline: none;
    width: 100%;
    min-width: 0;
    font-family: inherit;
    font-size: 14px;
    color: ${colors.neutral100};
    background: transparent;
    font-variant-numeric: tabular-nums;
  }
`;

export const ClearFieldButton = styled.button`
  display: inline-flex;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${colors.neutral40};

  svg {
    font-size: 16px;
  }

  &:hover {
    color: ${colors.neutral80};
  }
`;

// Multi-select and chip entry

export const MultiBox = styled.div<{ focused: boolean; invalid?: boolean; bare?: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 4px 8px;
  border: 1px solid
    ${({ focused, invalid }) => {
      if (invalid) return colors.red60;
      return focused ? colors.blue60 : colors.neutral30;
    }};
  box-shadow: ${({ focused }) => (focused ? `0 0 0 3px ${colors.blue10}` : 'none')};
  border-radius: 6px;
  background: ${colors.white};
  box-sizing: border-box;
  cursor: text;

  ${({ bare }) =>
    bare
      ? `border-color: transparent; padding-left: 0; min-height: 0; cursor: default; box-shadow: none;`
      : ''}

  > input {
    flex: 1;
    min-width: 140px;
    height: 28px;
    border: none;
    outline: none;
    font-family: inherit;
    font-size: 14px;
    color: ${colors.neutral100};
    background: transparent;
  }
`;

export const TokenChip = styled.span<{ tone: 'include' | 'exclude' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  /* Even padding centres a read-only chip; the remove button pulls itself into the right padding */
  padding: 0 8px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  background: ${({ tone }) => (tone === 'include' ? colors.blue5 : colors.red5)};
  color: ${({ tone }) => (tone === 'include' ? colors.blue70 : colors.red70)};
  white-space: nowrap;

  code {
    font-size: 12px;
    opacity: 0.75;
  }

  button {
    display: inline-flex;
    margin-right: -4px;
    border: none;
    background: transparent;
    padding: 1px;
    border-radius: 50%;
    color: inherit;
    opacity: 0.6;
    cursor: pointer;

    svg {
      font-size: 14px;
    }

    &:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.08);
    }
  }
`;

export const Dropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 260px;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: ${colors.white};
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

export const DropdownGroupLabel = styled.li`
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.neutral50};
`;

export const DropdownOption = styled.li<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 12px;
  font-size: 14px;
  color: ${colors.neutral100};
  background: ${({ active }) => (active ? colors.blue5 : 'transparent')};
  cursor: pointer;

  small {
    font-family: monospace;
    font-size: 12px;
    color: ${colors.neutral50};
  }
`;

export const DropdownEmpty = styled.li`
  padding: 10px 12px;
  font-size: 13px;
  color: ${colors.neutral50};
`;

export const DropdownError = styled(DropdownEmpty)`
  color: ${colors.red70};
`;

// Rule rows

/* react-querybuilder emits standard class names and ships no required CSS; lay them out here */
export const RulesShell = styled.div<{ empty: boolean; bordered: boolean }>`
  ${({ bordered }) =>
    bordered
      ? `
    border: 1px solid ${colors.neutral20};
    border-radius: 10px;
    background: ${colors.white};
  `
      : ''}

  .ruleGroup {
    display: flex;
    flex-direction: column;
  }

  .ruleGroup-header {
    display: none;
  }

  .ruleGroup-body {
    display: flex;
    flex-direction: column;
  }

  ${({ empty }) => (empty ? 'display: none;' : '')}
`;

export const RuleRowBox = styled.div<{ invalid: boolean }>`
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr) 28px;
  gap: 16px;
  align-items: start;
  padding: 14px 16px;
  border-bottom: 1px solid ${colors.neutral10};
  border-left: 3px solid ${({ invalid }) => (invalid ? colors.red60 : 'transparent')};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const RuleLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral100};
  padding-top: 7px;

  small {
    display: block;
    padding-top: 2px;
    font-size: 12px;
    font-weight: 400;
    color: ${colors.neutral50};
  }
`;

export const RuleEditors = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const RuleInline = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

export const RemoveRuleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.neutral50};
  cursor: pointer;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${colors.red5};
    color: ${colors.red60};
  }
`;

export const RuleLabelLine = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const notPill = `
  height: 20px;
  padding: 0 8px;
  border: 1px solid ${colors.neutral30};
  border-radius: 10px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.neutral70};
  background: ${colors.white};
`;

const notPillNegated = `
  border-color: ${colors.red20};
  color: ${colors.red70};
  background: ${colors.red5};
`;

export const NotToggle = styled.button`
  ${notPill}
  cursor: pointer;

  &[aria-pressed='true'] {
    ${notPillNegated}
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: 1px;
  }
`;

/* The read-only exclude marker: a pressed, disabled NotToggle that is plain text */
export const NotMarker = styled.span`
  ${notPill}
  ${notPillNegated}
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  opacity: 0.6;
`;

export const AddSideButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  font-family: inherit;
  font-size: 12px;
  color: ${colors.blue60};
  background: transparent;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: 2px;
  }
`;

export const Badge = styled.span<{ tone: 'proposed' }>`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${colors.neutral10};
  color: ${colors.neutral70};
  border: 1px solid ${colors.neutral30};
  white-space: nowrap;
`;
